/*
 * The one place the console decides that an admin API refusal means the operator's session is over.
 *
 * It is a module-level signal rather than a prop or a context because the admin API clients are
 * plain non-React modules called from screens, loaders and effects alike; threading a callback from
 * the auth provider down to every one of them would touch every client for no gain. The clients
 * report a status, the auth provider subscribes, and neither knows about the other.
 *
 * The distinction it owns: 401 means the token is no longer accepted, so the session is dead and
 * every tab should drop it. 403 means this operator may not do this thing — their session is fine,
 * and the screens already handle that refusal (NoAccessScreen, refusal-copy). Conflating the two
 * would sign an operator out for clicking something their role does not grant.
 *
 * Note the login and reset calls (auth/admin-auth-client) deliberately do NOT report here: a wrong
 * password is also a 401, and it must surface as the login screen's own refusal copy rather than as
 * a session-ending event.
 */

type UnauthorizedListener = () => void;

const listeners = new Set<UnauthorizedListener>();

/**
 * Report the status of a refused admin API call. Anything other than 401 is ignored, so call sites
 * stay a single unconditional line at the point they already build their failure result.
 */
export function notifyUnauthorized(status: number): void {
  if (status !== 401) {
    return;
  }
  for (const listener of [...listeners]) {
    listener();
  }
}

/** Listen for the session being refused. Returns the unsubscribe. */
export function subscribeUnauthorized(listener: UnauthorizedListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
